import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { BuilderShell } from "@/components/reporting/builder/builder-shell";
import { useAppStore } from "@/store";

type ReportQueryDocument = components["schemas"]["ReportQueryDocument"];
type ReportVisualization = components["schemas"]["ReportVisualization"];

/**
 * The one channel a document reaches an unsaved report through. It is declared
 * once, here, because this is the only route that reads it — every caller that
 * opens a drawing as a report navigates to `/reports/new` with it in the
 * location state, and the augmentation is what types both ends of that.
 */
declare module "@tanstack/react-router" {
  interface HistoryState {
    reportSeed?: { query: ReportQueryDocument; visualization: ReportVisualization };
  }
}

export const Route = createFileRoute("/_authenticated/reports/new")({
  component: NewReportRoute,
});

/**
 * A new report. The shell opens on the start screen, or on the document a
 * caller handed over in the location state, so this route fetches nothing — it
 * hands over the org and that document, and takes the exits back.
 *
 * The builder is one of the surfaces that drops the page gutter, and it sizes
 * itself against a full-height parent. The authenticated shell reserves `pb-24`
 * under every page so the fixed quick-actions launcher never lands on a page's
 * trailing control; the builder has no trailing control and would read that
 * reserve as a dead band, so this frame grows by exactly the reserve and pulls
 * it back — 6rem is `pb-24`, and the two move together.
 */
function NewReportRoute() {
  const { t } = useTranslation("org");
  const navigate = useNavigate();
  const orgId = useAppStore((s) => s.activeOrgId);
  const reportSeed = useRouterState({ select: (s) => s.location.state.reportSeed });

  const backToList = () => {
    void navigate({ to: "/reports" });
  };

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext")}</p>
      </div>
    );
  }

  return (
    <div className="-mb-24 h-[calc(100%+6rem)] print:mb-0 print:h-full">
      <BuilderShell
        orgId={orgId}
        {...(reportSeed === undefined ? {} : { initialDocument: reportSeed })}
        onSaved={(reportId) => {
          void navigate({ to: "/reports/$id", params: { id: reportId } });
        }}
        onDiscard={backToList}
        onBack={backToList}
        onOpenReports={backToList}
      />
    </div>
  );
}
